import { z } from 'zod';

import {
  closePayPeriodPermissionKey,
  overtimePolicyTypeValues,
  payPeriodTypeValues,
  weekStartsOnValues,
  type ClosePayPeriodPermissionKey,
  type GetTimeTrackingSettingsRequest,
  type GetTimeTrackingSettingsResponse,
  type OvertimePolicyDto,
  type OvertimePolicyType,
  type PayPeriodType,
  type TimeTrackingSettingsDto,
  type UpdateTimeTrackingSettingsRequest,
  type UpdateTimeTrackingSettingsResponse,
  type WeekStartsOn,
} from './time-tracking-settings.dto.js';

const timestampSchema = z.iso.datetime();

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const PayPeriodTypeSchema: z.ZodType<PayPeriodType> = z.enum(payPeriodTypeValues);

export const WeekStartsOnSchema: z.ZodType<WeekStartsOn> = z.union([
  z.literal(weekStartsOnValues[0]),
  z.literal(weekStartsOnValues[1]),
  z.literal(weekStartsOnValues[2]),
  z.literal(weekStartsOnValues[3]),
  z.literal(weekStartsOnValues[4]),
  z.literal(weekStartsOnValues[5]),
  z.literal(weekStartsOnValues[6]),
]);

export const OvertimePolicyTypeSchema: z.ZodType<OvertimePolicyType> = z.enum(overtimePolicyTypeValues);

export const OvertimePolicySchema: z.ZodType<OvertimePolicyDto> = z.object({
  type: OvertimePolicyTypeSchema,
  thresholdMinutes: z.number().int().nonnegative(),
});

export const ClosePayPeriodPermissionKeySchema: z.ZodType<ClosePayPeriodPermissionKey> = z.literal(
  closePayPeriodPermissionKey,
);

export const TimeTrackingSettingsDtoSchema: z.ZodType<TimeTrackingSettingsDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  payPeriodType: PayPeriodTypeSchema,
  timezone: z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' }),
  weekStartsOn: WeekStartsOnSchema,
  overtimePolicy: OvertimePolicySchema,
  closePeriodRequiresPermission: ClosePayPeriodPermissionKeySchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const GetTimeTrackingSettingsRequestSchema: z.ZodType<GetTimeTrackingSettingsRequest> = z.object({
  companyId: z.uuid(),
});

export const GetTimeTrackingSettingsResponseSchema: z.ZodType<GetTimeTrackingSettingsResponse> = z.object({
  settings: TimeTrackingSettingsDtoSchema,
});

export const UpdateTimeTrackingSettingsRequestSchema: z.ZodType<UpdateTimeTrackingSettingsRequest> = z.object({
  companyId: z.uuid(),
  payPeriodType: PayPeriodTypeSchema,
  timezone: z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' }),
  weekStartsOn: WeekStartsOnSchema,
  overtimePolicy: OvertimePolicySchema,
});

export const UpdateTimeTrackingSettingsResponseSchema: z.ZodType<UpdateTimeTrackingSettingsResponse> = z.object({
  settings: TimeTrackingSettingsDtoSchema,
});

